const CHUNK_RELOAD_STORAGE_KEY = 'chunk-load-recovery:last-attempt-at';
export const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
    /chunkloaderror/i,
    /loading chunk .* failed/i,
    /loading css chunk .* failed/i,
    /error loading chunk/i,
    /failed to fetch dynamically imported module/i,
    /importing a module script failed/i,
];

const CHUNK_ASSET_URL_PATTERN = /\/static\/(js|css)\/.+\.(chunk\.)?(js|css)(\?.*)?$/i;

const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }

    if (error instanceof Error) {
        return `${error.name} ${error.message}`;
    }

    if (error && typeof error === 'object') {
        const objectError = error as { message?: unknown; reason?: unknown; name?: unknown };

        if (typeof objectError.message === 'string') {
            return objectError.message;
        }

        if (typeof objectError.name === 'string') {
            return objectError.name;
        }

        if (objectError.reason) {
            return getErrorMessage(objectError.reason);
        }
    }

    return '';
};

export const isChunkAssetUrl = (url: string): boolean => CHUNK_ASSET_URL_PATTERN.test(url);

export const isChunkLoadError = (error: unknown): boolean => {
    const message = getErrorMessage(error);

    if (typeof error === 'string' && isChunkAssetUrl(error)) {
        return true;
    }

    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

export const shouldAttemptChunkReload = (
    lastAttemptAt: number | null,
    now: number = Date.now(),
    cooldownMs: number = CHUNK_RELOAD_COOLDOWN_MS
): boolean => {
    if (lastAttemptAt === null || Number.isNaN(lastAttemptAt)) {
        return true;
    }

    return now - lastAttemptAt > cooldownMs;
};

const getLastChunkReloadAttempt = (): number | null => {
    try {
        const storedValue = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);

        if (!storedValue) {
            return null;
        }

        const parsedValue = Number(storedValue);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    } catch {
        return null;
    }
};

const setLastChunkReloadAttempt = (value: number): void => {
    try {
        window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(value));
    } catch {
        // no-op
    }
};

const cleanupCachesAndServiceWorkers = async (): Promise<void> => {
    const cleanupTasks: Array<Promise<unknown>> = [];

    if (typeof window !== 'undefined' && 'caches' in window) {
        cleanupTasks.push(
            caches.keys().then((cacheNames) => Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName))))
        );
    }

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        cleanupTasks.push(
            navigator.serviceWorker
                .getRegistrations()
                .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        );
    }

    if (cleanupTasks.length > 0) {
        await Promise.allSettled(cleanupTasks);
    }
};

export const recoverFromChunkLoadError = (error: unknown, options?: { force?: boolean }): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const force = Boolean(options?.force);

    if (!force && !isChunkLoadError(error)) {
        return false;
    }

    const now = Date.now();

    if (!force && !shouldAttemptChunkReload(getLastChunkReloadAttempt(), now)) {
        return false;
    }

    setLastChunkReloadAttempt(now);

    void cleanupCachesAndServiceWorkers().finally(() => {
        window.location.reload();
    });

    return true;
};

let listenersInstalled = false;

const getResourceUrlFromEvent = (event: Event): string | null => {
    const target = event.target as EventTarget | null;

    if (!target) {
        return null;
    }

    if (target instanceof HTMLScriptElement) {
        return target.src || null;
    }

    if (target instanceof HTMLLinkElement) {
        return target.href || null;
    }

    return null;
};

export const installChunkLoadErrorRecoveryListeners = (): void => {
    if (typeof window === 'undefined' || listenersInstalled) {
        return;
    }

    listenersInstalled = true;

    window.addEventListener(
        'error',
        (event: Event) => {
            const resourceUrl = getResourceUrlFromEvent(event);

            if (resourceUrl && !isChunkAssetUrl(resourceUrl)) {
                return;
            }

            const errorEvent = event as ErrorEvent;
            const candidateError = errorEvent.error ?? errorEvent.message ?? resourceUrl;
            recoverFromChunkLoadError(candidateError);
        },
        true
    );

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const wasRecovered = recoverFromChunkLoadError(event.reason);

        if (wasRecovered) {
            event.preventDefault();
        }
    });
};
